/**
 * Cross-vendor device schema.
 *
 * Devices flow in from each vendor's own per-network endpoint, but the
 * /devices view normalizes them into a single shape so the inventory table
 * is one row per device regardless of vendor. The vendor field is the
 * discriminator that drives icon + per-vendor-detail rendering.
 */
import { z } from "zod";

const DeviceVendorSchema = z.enum(["meraki", "aruba", "shasta"]);
export type DeviceVendor = z.infer<typeof DeviceVendorSchema>;

/** Normalized status — every vendor's various statuses fold into one of these. */
const DeviceStatusSchema = z.enum(["online", "alerting", "offline", "unknown"]);
export type DeviceStatus = z.infer<typeof DeviceStatusSchema>;

/**
 * One row in the cross-vendor inventory. Most fields are optional because
 * not every vendor returns every field — the table renders "—" gracefully
 * for missing data.
 */
const InventoryDeviceSchema = z.object({
  vendor: DeviceVendorSchema,
  serial: z.string(),
  name: z.string().nullable(),
  model: z.string().optional(),
  mac: z.string().optional(),
  status: DeviceStatusSchema,
  /** Friendly site/network label — e.g. "marriott-nyc-21-prod" or "PAR-09 IDF". */
  site: z.string().nullable(),
  /** Vendor-specific group/network identifier; just for display. */
  groupOrNetwork: z.string().nullable().optional(),
  ipAddress: z.string().nullable().optional(),
  firmware: z.string().optional(),
  tags: z.array(z.string()).optional(),
  /** Ratio 0-1 of recent uptime — drives the trailing health bar. */
  healthRatio: z.number().min(0).max(1).optional(),
});
export type InventoryDevice = z.infer<typeof InventoryDeviceSchema>;
export const InventoryDeviceListSchema = z.array(InventoryDeviceSchema);
